import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LoginButton } from './LoginButton';
import './Layout.css';

export default function Layout({ children }: { children: React.ReactNode }) {
  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has a saved preference, otherwise default to dark mode
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme ? savedTheme === 'dark' : true; // Default to dark mode
    setDarkMode(isDark);
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, []);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    document.documentElement.setAttribute('data-theme', newMode ? 'dark' : 'light');
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  return (
    <div className="layout">
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-left">
            <Link to="/" className="navbar-brand">
              <img src="/img/logo.svg" alt="DayZ Scripts" className="navbar-logo" />
            </Link>
            <div className="navbar-items">
              <a
                href="#"
                className="navbar-link"
                onClick={(e) => {
                  e.preventDefault();
                  if (location.pathname === '/docs-spa') {
                    window.location.reload();
                  } else {
                    navigate('/docs-spa');
                  }
                }}
              >
                Class Browser
              </a>
              <Link to="/about" className="navbar-link">About</Link>
              {user?.is_moderator && (
                <Link to="/moderation" className="navbar-link">Moderation</Link>
              )}
              {user?.is_admin && (
                <Link to="/admin" className="navbar-link">Admin</Link>
              )}
            </div>
          </div>

          <div className="navbar-right">
            <button
              onClick={toggleDarkMode}
              className="theme-toggle theme-toggle-desktop"
              aria-label="Toggle dark mode"
            >
              {darkMode ? '☀️' : '🌙'}
            </button>

            <div className="login-button-desktop">
              <LoginButton />
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="mobile-menu-toggle"
              aria-label="Toggle mobile menu"
            >
              ☰
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <>
            <div
              className="mobile-menu-overlay"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="mobile-menu">
              <div className="mobile-menu-header">
                {user && (
                  <div className="mobile-menu-welcome">
                    Welcome back!
                  </div>
                )}
                <LoginButton />
              </div>

              <div className="mobile-menu-divider"></div>

              <a
                href="#"
                className="mobile-menu-link"
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  if (location.pathname === '/docs-spa') {
                    window.location.reload();
                  } else {
                    navigate('/docs-spa');
                  }
                }}
              >
                📚 Class Browser
              </a>
              <Link
                to="/about"
                className="mobile-menu-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                ℹ️ About
              </Link>
              {user?.is_moderator && (
                <Link
                  to="/moderation"
                  className="mobile-menu-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  👑 Moderation
                </Link>
              )}
              {user?.is_admin && (
                <Link
                  to="/admin"
                  className="mobile-menu-link"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  🔧 Admin
                </Link>
              )}

              <div className="mobile-menu-divider"></div>

              <button
                onClick={() => {
                  toggleDarkMode();
                  setMobileMenuOpen(false);
                }}
                className="mobile-menu-link mobile-menu-button"
              >
                {darkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
              </button>
            </div>
          </>
        )}
      </nav>

      <main className="main-content">
        {children}
      </main>
    </div>
  );
}
